// The order lifecycle, mirroring the backend `OrderStatus` enum (ADR 0006).
//
// Array order is flow order and is what every picker renders; the `value` is the
// database id and is deliberately *not* in sequence, because renumbering would
// have rewritten `orders.status` and 56k history rows for nothing.
//
// `retired: true` means "still labelled, never offered": QA left the lifecycle
// but its history entries must keep resolving to a name rather than "Unknown".
export const OrderStatusEnum = Object.freeze([
  {
    // DRAFT is the initial state — no "set to DRAFT" permission; only the
    // umbrella CHANGE_STATUS can move an order back to it.
    label: 'DRAFT',
    value: 1,
    color: '#757575'
  },
  {
    label: 'APPROVED',
    value: 2,
    color: '#492E87',
    permission:'ORDER_APPROVE'
  },
  {
    label: 'PRODUCTION',
    value: 3,
    color: '#FFD700',
    permission:'ORDER_IN_PRODUCTION'
  },
  {
    label: 'READY',
    value: 5,
    color: '#2196F3',
    permission:'ORDER_READY'
  },
  {
    // The courier booking, made once the goods are ready.
    label: 'BOOKING',
    value: 9,
    color: '#800080',
    permission:'ORDER_BOOKING'
  },
  {
    label: 'OUT FOR DELIVERY',
    value: 10,
    color: '#0EA5E9',
    permission:'ORDER_OUT_FOR_DELIVERY'
  },
  {
    // Parked for a repair or a reschedule; releasing returns it to the status it
    // was held from, which the backend remembers in `held_from_status`.
    label: 'ON HOLD',
    value: 11,
    color: '#F59E0B',
    permission:'ORDER_HOLD'
  },
  {
    label: 'DELIVERED',
    value: 6,
    color: '#008000',
    permission:'ORDER_DELIVERED'
  },
  {
    label: 'RETURNED',
    value: 7,
    color: '#FF0000',
    permission:'ORDER_RETURNED'
  },
  {
    label: 'CANCELLED',
    value: 8,
    color: '#696969',
    permission:'ORDER_CANCELLED'
  },
  {
    label: 'QA',
    value: 4,
    color: '#FFA500',
    permission: 'ORDER_IN_QA',
    retired: true
  }
])

// The exits: a status nobody should be able to enter without saying why, and
// the ones that put finished goods back into Ready Stock. Mirrors
// OrderStatus::requiresReason() on the backend, which enforces it.
export const EXIT_STATUSES = Object.freeze([8, 7, 11]);

// Statuses from which finished goods exist, so cancelling or returning has
// something to restock — mirrors OrderStatus::producedForRestock().
export const PRODUCED_STATUSES = Object.freeze([5, 9, 10, 11, 6]);

// Open/active: committed but not finished. Mirrors OrderStatus::open().
export const OPEN_STATUSES = Object.freeze([2, 3, 5, 9, 10, 11]);

// Worth filtering a list by. Retired statuses hold no orders once their
// migration has run, so offering one only ever returns an empty page — but
// `statusMeta` must still resolve them, which is why they stay in the array.
export const filterableStatuses = OrderStatusEnum.filter((s) => !s.retired);

export const requiresReason = (status) => EXIT_STATUSES.includes(status);

export const restocksFrom = (currentStatus) => PRODUCED_STATUSES.includes(currentStatus);

// Transition-scoped status permissions (ADR 0004), mirroring the backend's
// StatusChangeAuthorizer: a user may move an order into a status if they hold
// the umbrella CHANGE_STATUS, or that status's own permission.
export const canSetStatus = (permissions = [], status) =>
  permissions.includes('CHANGE_STATUS') ||
  (!!status?.permission && permissions.includes(status.permission));

// The subset of statuses the user is allowed to set — use this to build any
// status picker so web and mobile share one barrier. Retired statuses are never
// offered, whatever permissions the user holds.
export const settableStatuses = (permissions = []) =>
  OrderStatusEnum.filter((s) => !s.retired && canSetStatus(permissions, s));

// Whether the user can set at least one status (gates the status control itself).
export const canChangeAnyStatus = (permissions = []) =>
  settableStatuses(permissions).length > 0;
